/*
 * CSL BRANDS Mentoring Program — French copy.
 *
 * Structure mirrors the reference editorial layout: a shared chrome (nav,
 * marquee, closing CTA, footer) plus one object per page. Every CTA carries a
 * `target` key instead of a href so the page component can resolve the correct
 * locale-prefixed path.
 */

import { PROJECT_LINKS } from '../../../lib/projectLinks';

const shared = {
  marquee: [
    "Candidature ouverte",
    "Douala // Cameroun",
    "10 places",
    "6 mois",
    "1:1 toutes les 2 semaines",
  ],
  cohortFacts: [
    { label: "Début", value: "Janvier 2027" },
    { label: "Lieu", value: "Douala, Cameroun" },
    { label: "Durée", value: "6 mois" },
    { label: "Places", value: "10 places" },
  ],
  cta: {
    kicker: "Prochaine promo — Janvier 2027",
    title: "Dix places. Six mois. A vous de jouer",
    text: "Le mentorat commence par une conversation. Dites-nous où vous en êtes et ce que vous voulez construire — nous répondons à chaque message.",
    primary: { label: "Postuler au Cohort 01", target: "contact" },
    secondary: { label: "Parler à l'équipe", target: "contact" },
  },
  footer: {
    description:
      "Le programme de mentorat CSL BRANDS est un parcours de six mois à Douala, et la base opérationnelle d'un foyer plus large pour les talents, les fondateurs et les ventures technologiques au Cameroun.",
    siteTitle: "Site",
    reachTitle: "Nous joindre",
    bottomLeft: "© 2026 CSL BRANDS. Programme de mentorat.",
    legal: [
      { label: "Confidentialité", target: "hostPrivacy" },
      { label: "Conditions", target: "hostTerms" },
      { label: "Plan du site", target: "hostSitemap" },
    ],
    social: [
      { label: "LinkedIn", href: "https://www.linkedin.com/company/kursa-brands/" },
      { label: "Facebook", href: "https://web.facebook.com/getkursa/" },
    ],
  },
};

const projects = [
  {
    key: "kursa",
    label: "CSL BRANDS / Produits / KURSA",
    tag: "Produit CSL BRANDS",
    title: "KURSA",
    text: "Plateforme de formation pour créer et vendre des cours en ligne.",
    href: PROJECT_LINKS.kursa,
  },
  {
    key: "primecorn",
    label: "CSL BRANDS / Produits / PrimeCorn",
    tag: "Produit CSL BRANDS",
    title: "PrimeCorn",
    text: "Outils ERP et CRM pour gérer l'activité d'une entreprise au même endroit.",
    href: PROJECT_LINKS.primecorn,
  },
  {
    key: "tchopnjoh",
    label: "CSL BRANDS / Produits / TchopNjoh",
    tag: "Produit CSL BRANDS",
    title: "TchopNjoh",
    text: "Logiciel de restauration pour les commandes, menus, stocks et programmes de fidélité.",
    href: PROJECT_LINKS.tchopnjoh,
  },
];

export const fr = {
  locale: "fr",
  seo: {
    index: {
      title: "Programme de mentorat CSL BRANDS — Douala, Cameroun",
      description:
        "Six mois de mentorat 1:1 à Douala : formation structurée, projets d'équipe et livraison par sprints, encadrés par des ingénieurs en activité.",
    },
    program: {
      title: "Le programme — Programme de mentorat CSL BRANDS",
      description:
        "Programme complet : pour qui, modèle d'apprentissage, curriculum, mentorat 1:1, sélection, engagement et suites de parcours.",
    },
    projects: {
      title: "Projets et talents — Programme de mentorat CSL BRANDS",
      description:
        "Découvrez les produits CSL BRANDS et ouvrez les mêmes liens que dans la vitrine de notre site principal.",
    },
    about: {
      title: "À propos — Programme de mentorat CSL BRANDS",
      description:
        "Où nous travaillons, comment nous opérons, l'écosystème CSL BRANDS, notre histoire et la vision d'un espace plus large à Douala.",
    },
  },
  nav: {
    skipToContent: "Aller au contenu",
    menu: "Menu",
    close: "Fermer",
    brand: "Programme de mentorat",
    // Short form for the host site's top navbar, which has to fit nine items
    // on one row at 1280px. Kept in sync with brand via the content test.
    navLabel: "Mentorat",
    brandName: "CSL BRANDS",
    links: [
      { pageKey: "index", label: "Accueil" },
      { pageKey: "program", label: "Programme" },
      { pageKey: "projects", label: "Projets" },
      { pageKey: "about", label: "À propos" },
    ],
    home: "Retour au site CSL BRANDS",
    languageLabel: "Langue",
    languageFr: "Français",
    languageEn: "English",
    openCta: "Postuler",
  },
  shared,
  index: {
    hero: {
      ticker: ["Pipeline // Cohort 01", "Douala // Cameroun", "Mentorat // 1:1"],
      eyebrow: "Programme de mentorat · CSL BRANDS",
      titleLines: ["Former seul.", "Livrer en équipe."],
      lead: "Le programme de mentorat CSL BRANDS est un parcours de six mois à Douala où des ingénieurs émergents sont accompagnés par un ingénieur en activité, en sessions 1:1 toutes les deux semaines — et où les entreprises recrutent des juniors qui savent déjà travailler en équipe.",
      primary: { label: "Postuler au Cohort 01", target: "contact" },
      secondary: { label: "Comprendre le programme", target: "program" },
      facts: shared.cohortFacts,
    },
    pipeline: {
      label: "Le parcours, étape par étape",
      steps: [
        { number: "01", title: "Candidature", text: "Rolling apply" },
        { number: "02", title: "Sélection", text: "2 semaines" },
        { number: "03", title: "Piscine", text: "2 semaines" },
        { number: "04", title: "Programme", text: "4 mois" },
        { number: "05", title: "Projet d'équipe", text: "2 mois" },
        { number: "06", title: "Stage", text: "6 mois" },
      ],
    },
    stats: [
      { value: "10", unit: "Places" },
      { value: "2", unit: "Sem. Piscine" },
      { value: "4", unit: "Mois mentorat" },
      { value: "2", unit: "Mois projet" },
    ],
    gap: {
      marker: "01",
      eyebrow: "L'écart",
      title: "Terminer un cours n'est pas être prêt pour une équipe.",
      lead: "Beaucoup de talents maîtrisent les outils sans avoir jamais livré dans un vrai flux de travail — pensée systémique, revue de code, discipline de sprint, décisions produit, un travail que d'autres doivent utiliser et maintenir.",
      cards: [
        { key: "A", title: "Les cours seuls", text: "Syntaxe, petites applications, travail solitaire. Des connaissances sans contexte d'équipe." },
        { key: "B", title: "La couche manquante", text: "Revues, sprints, contrats, arbitrages, communication, livraison." },
        { key: "C", title: "Prêt pour l'équipe", text: "Contribuer à des systèmes réels avec une discipline sur laquelle on peut compter." },
      ],
    },
    journey: {
      marker: "02",
      eyebrow: "Le parcours",
      title: "Six mois. Vrais processus. Petit groupe.",
      lead: "Quatre mois de formation mentorée et structurée, puis deux mois à livrer un projet d'équipe — les conditions d'une vraie équipe d'ingénierie, avant d'en rejoindre une.",
      link: { label: "Découvrir le programme", target: "program" },
      steps: [
        { number: "01", title: "Découvrir et sélectionner", text: "Nous cherchons de la curiosité, de la discipline, une base solide et l'envie d'apprendre en faisant. Petites promotions, sélection délibérée." },
        { number: "02", title: "Préparer", text: "Les candidats sélectionnés suivent une formation préparatoire assignée et mettent en place les outils et les habitudes sur lesquelles la promo va tourner." },
        { number: "03", title: "Bâtir les fondations", text: "Concepts clés d'ingénierie, de produit et de collaboration — du contrôle de version à la structure d'un système." },
        { number: "04", title: "Travailler en équipe", text: "Planifier des solutions, écrire des user stories, définir des domaines et des API, travailler par sprints, relire du code, répondre aux retours." },
        { number: "05", title: "Livrer des projets", text: "Construire des solutions utiles à des problèmes locaux réels. Montrer le travail, le processus et les apprentissages." },
        { number: "06", title: "Transitionner", text: "Évoluer vers un stage, un poste chez CSL BRANDS, un emploi externe, une spécialisation ou un projet de fondateur." },
      ],
    },
    proof: {
      marker: "03",
      eyebrow: "Ce que nous créons",
      title: "Découvrez les produits CSL BRANDS.",
      link: { label: "Tous les projets", target: "projects" },
      projects,
    },
    mentoring: {
      marker: "04",
      eyebrow: "Ce que le mentorat apporte",
      title: "Des juniors qui arrivent déjà en train de travailler en équipe.",
      lead: "Chaque mentoringé passe par quatre mois de formation encadrée par un mentor assigné, puis deux mois de développement produit en équipe sur un projet réel. À la fin, ils ont travaillé en environnement collaboratif, livré des logiciels selon des workflows Git et agiles, et acquis les habitudes qu'une équipe moderne attend.",
      items: [
        { number: "01", title: "Discipline Git", text: "Branches, pull requests et revue de code comme habitude quotidienne — pas comme théorie." },
        { number: "02", title: "Livraison agile", text: "User stories, sprints, points quotidiens et démos tout au long des six mois." },
        { number: "03", title: "Travail en équipe", text: "Quatre mois mentorés, deux mois à livrer ensemble un même produit." },
        { number: "04", title: "Un mentor, pas un corrigé", text: "Un ingénieur en activité qui relit votre travail et vous confronte à vos décisions." },
        { number: "05", title: "Des preuves, pas des promesses", text: "Consultez les projets, les dépôts, et la façon dont chaque équipe a réellement travaillé." },
      ],
    },
    vision: {
      kicker: "Nous construisons",
      title: "Un foyer fiable pour les talents, les fondateurs et les ventures technologiques au Cameroun.",
      lead: "Le programme de mentorat est la base opérationnelle. CSL BRANDS fait tourner la formation à côté d'un vrai travail produit, dans des espaces à Douala — salles de formation, postes de travail, événements. Nous annonçons chaque ouverture quand elle a lieu, pas avant.",
      link: { label: "La vision", target: "about" },
    },
    waysIn: {
      marker: "05",
      eyebrow: "Trouvez votre porte d'entrée",
      title: "Quatre façons de rejoindre le programme.",
      cards: [
        { key: "mentore", tag: "Pour les mentoringés", title: "Postuler", text: "Candidature au Cohort 01 — démarrage en janvier 2027.", cta: "Postuler", target: "contact" },
        { key: "mentor", tag: "Pour les mentors", title: "Devenir mentor", text: "Guider un mentoringé en sessions 1:1 toutes les deux semaines.", cta: "Devenir mentor", target: "contact" },
        { key: "employer", tag: "Pour les employeurs", title: "Rencontrer des talents", text: "Examiner des travaux réels et rencontrer des juniors prêts.", cta: "Rencontrer des talents", target: "contact" },
        { key: "sponsor", tag: "Pour les sponsors", title: "Soutenir une promo", text: "Financer dix carrières, documentées publiquement.", cta: "Soutenir une promo", target: "contact" },
      ],
    },
  },
  program: {
    hero: {
      ticker: ["Mentorat // 1:1 toutes les 2 semaines", "Douala // Cameroun", "6 mois"],
      eyebrow: "Le programme",
      titleLines: ["Le programme de mentorat."],
      lead: "Six mois dans les conditions d'une vraie équipe d'ingénierie : cours structurés, mentorat 1:1 toutes les deux semaines, projets d'équipe et livraison par sprints — du travail que vous pouvez montrer et des habitudes auxquelles une équipe peut se fier.",
      primary: { label: "Postuler au Cohort 01", target: "contact" },
      secondary: { label: "Poser une question", target: "contact" },
      facts: shared.cohortFacts,
    },
    audience: {
      marker: "01",
      eyebrow: "Pour qui",
      title: "Pour les ingénieurs au début de leur montée.",
      lead: "Le programme s'adresse à celles et ceux qui ont déjà des fondations et qui veulent ajouter la couche d'équipe : workflows, revues, livraison et jugement.",
      profilesTitle: "Profils",
      profiles: [
        { title: "Diplômés", text: "Diplômés en ingénierie d'une école technique ou d'une université." },
        { title: "Dernière année", text: "Étudiants en dernière année d'ingénierie quiEffectuent leur période de stage." },
        { title: "Autodidactes", text: "Apprenants autonomes et reconversions avec des fondations équivalentes — argumentez dans la lettre de motivation." },
      ],
      needsTitle: "Ce qu'il vous faut",
      needs: [
        { title: "Un ordinateur personnel", text: "Votre propre machine, configurée comme votre atelier quotidien." },
        { title: "Un compte GitHub", text: "Votre travail vit dans le contrôle de version dès le premier jour." },
        { title: "Être à Douala", text: "Présence en personne, tous les jours, pendant tout le programme." },
        { title: "Un engagement réel", text: "Cours, projets d'équipe, sprints — six mois de présence." },
      ],
    },
    model: {
      marker: "02",
      eyebrow: "Le modèle d'apprentissage",
      title: "Six étapes, de la sélection à la transition.",
      lead: "Chaque étape fait monter le niveau : des fondations, au travail en équipe, jusqu'à livrer un travail que d'autres utilisent.",
      steps: [
        { number: "01", title: "Découvrir et sélectionner", text: "Nous cherchons de la curiosité, de la discipline, une base solide et l'envie d'apprendre en faisant. Petites promotions, sélection délibérée." },
        { number: "02", title: "Préparer", text: "Les candidats sélectionnés suivent une formation préparatoire assignée et mettent en place les outils et les habitudes sur lesquelles la promo va tourner." },
        { number: "03", title: "Bâtir les fondations", text: "Concepts clés d'ingénierie, de produit et de collaboration — du contrôle de version à la structure d'un système." },
        { number: "04", title: "Travailler en équipe", text: "Planifier des solutions, écrire des user stories, définir des domaines et des API, travailler par sprints, relire du code, répondre aux retours." },
        { number: "05", title: "Livrer des projets", text: "Construire des solutions utiles à des problèmes locaux réels. Montrer le travail, le processus et les apprentissages." },
        { number: "06", title: "Transitionner", text: "Évoluer vers un stage, un poste chez CSL BRANDS, un emploi externe, une spécialisation ou un projet de fondateur." },
      ],
    },
    curriculum: {
      marker: "03",
      eyebrow: "Le contenu",
      title: "Des compétences, pas une liste d'outils.",
      lead: "Tout ce qui suit s'utilise ensemble dans de vrais workflows d'équipe — sur des projets, sous revue, dans des sprints. Rien n'est enseigné comme un cours isolé et abandonné sur une étagère.",
      groups: [
        { title: "Fondations", items: ["Environnement Linux", "Git & GitHub", "HTML & CSS", "JavaScript", "NPM"] },
        { title: "Ingénierie", items: ["Go", "React", "Next.js", "Bases relationnelles", "Design d'API", "Authentification & autorisation"] },
        { title: "Systèmes", items: ["Design patterns", "Domain-Driven Design", "Frontières de domaine", "Contrats d'API", "Arbitrages de maintenabilité"] },
        { title: "Livraison", items: ["Agile & Scrum", "User stories & sprints", "Lint & tests", "CI/CD avec GitHub Actions", "Pull requests & revue", "Refactoring"] },
        { title: "Professionnel", items: ["Idéation produit", "Exposition UX", "Communication", "Travail en équipe", "Book Club", "Démos publiques"] },
      ],
    },
    mentorship: {
      marker: "04",
      eyebrow: "Le mentorat",
      title: "Un ingénieur en activité dans votre coin.",
      lead: "Chaque mentoringé est associé à un mentor pour toute la durée du programme — des sessions structurées toutes les deux semaines pour relire le travail, questionner les décisions et confronter votre raisonnement à l'expérience réelle.",
      facts: [
        { label: "Cadence", value: "Sessions 1:1 toutes les deux semaines" },
        { label: "Qui mentor", value: "Des ingénieurs et praticiens en activité" },
        { label: "Ce qui se passe", value: "Revue de travail, contexte réel, orientation" },
      ],
      pillars: [
        { key: "A", title: "Relire le travail", text: "Le code, les décisions et les arbitrages sont regardés par quelqu'un qui livre professionnellement." },
        { key: "B", title: "Donner du contexte réel", text: "Comment les équipes planifient vraiment, se disputent, se rattrapent et livrent — au-delà de ce que couvrent les cours." },
        { key: "C", title: "Guider le chemin", text: "Orientation de carrière, spécialisation, et un retour honnête sur votre niveau de préparation." },
      ],
      expertiseTitle: "Les domaines que nos mentors couvrent",
      expertiseLead: "Nous constituons les binômes en fonction du projet et des objectifs de chaque mentoringé. Voici les expertises mobilisées sur les promotions précédentes.",
      expertise: [
        { title: "Backend & systèmes distribués", text: "Go, architectures qui tiennent sous charge, contrats d'API et migrations sans downtime." },
        { title: "Frontend & design systems", text: "React, Next.js, accessibilité et composants qu'une équipe entière peut faire évoluer." },
        { title: "Données & produit", text: "Modélisation, pipelines de données, et transformer une idée en problème correctement formulé." },
        { title: "DevOps & plateforme", text: "CI/CD, infrastructure as code, observabilité et déploiements qui ne réveillent personne à 3 h du matin." },
        { title: "QA & sécurité applicative", text: "Tests, revue de sécurité applicative, et les erreurs que l'on n'attrape pas en production." },
        { title: "Carrière & architecture", text: "Revue d'architecture, préparation aux entretiens, et arbitrer entre stabilité et vitesse." },
      ],
      cta: { label: "Devenir mentor", target: "contact" },
    },
    selection: {
      marker: "05",
      eyebrow: "La sélection",
      title: "Délibérée, en personne, compétitive.",
      lead: "De petites promotions — 10 places — signifient que chaque place se mérite. Le processus lui-même est le premier test de l'engagement.",
      steps: [
        { number: "01", title: "Candidater", text: "Envoyez votre parcours, votre motivation et votre lettre." },
        { number: "02", title: "Sélection", text: "Revue de candidature, travail préparatoire assigné, puis entretien en personne." },
        { number: "03", title: "Piscine", text: "Un défi de deux semaines, tout ou rien — un test de la passion, pas du niveau." },
        { number: "04", title: "Programme", text: "Six mois de cours structurés, de mentorat et de livraison en équipe." },
        { number: "05", title: "Stage", text: "Six mois dans du travail réel — le pont vers l'industrie." },
      ],
    },
    commitment: {
      marker: "06",
      eyebrow: "L'engagement",
      title: "Sachez ce que vous signez.",
      lead: "Le programme demande beaucoup, volontairement. Voici le deal, en termes clairs.",
      facts: [
        { label: "Piscine", value: "2 semaines, tout ou rien — avant le programme" },
        { label: "Durée", value: "6 mois, structurés" },
        { label: "Lieu", value: "Douala, Cameroun — en personne, tous les jours" },
        { label: "Taille de promo", value: "~10 mentoringés, sélection" },
        { label: "Mentorat", value: "Sessions 1:1 toutes les deux semaines" },
        { label: "Structure", value: "4 mois de formation mentorée + 2 mois de projet d'équipe" },
        { label: "Matériel", value: "Ordinateur personnel + compte GitHub" },
        { label: "Après le programme", value: "Stage de 6 mois et transition vers l'industrie" },
      ],
    },
    after: {
      marker: "07",
      eyebrow: "Après le programme",
      title: "Un chemin, pas une promesse.",
      lead: "La phase de six mois ouvre sur des parcours de stage et de transition. Où chaque mentoringé atterrit dépend de son travail, du marché et des opportunités que nous ouvrons ensemble.",
      note: "Nous ne promettons pas d'emplois. Nous construisons les preuves — de vrais projets, une vraie discipline de travail — qui les méritent.",
      steps: [
        { number: "01", title: "Stage", text: "Une phase de stage structurée pour appliquer la discipline en pratique." },
        { number: "02", title: "CSL BRANDS", text: "Contribuer à des projets d'ingénierie CSL BRANDS là où il y a des ouvertures." },
        { number: "03", title: "Emploi externe", text: "Mises en relation avec des employeurs qui évaluent des preuves, pas des certificats." },
        { number: "04", title: "Spécialisation", text: "Approfondissement dans la direction que votre travail de projet a indiquée." },
        { number: "05", title: "Projet de fondateur", text: "Construire sur votre propre idée, là où l'adéquation et le soutien existent." },
      ],
    },
    faq: {
      marker: "08",
      eyebrow: "FAQ",
      title: "Demandé, répondu.",
      lead: "Quelque chose manque ? Posez-nous la question directement — nous répondons à chaque message.",
      link: { label: "Nous contacter", target: "contact" },
      items: [
        { question: "Qui peut postuler ?", answer: "Les diplômés en ingénierie, les étudiants en dernière année en période de stage, et les apprenants autonomes ou en reconnexion avec des fondations équivalentes. Dans ce dernier cas, votre lettre de motivation doit faire la démonstration." },
        { question: "Quelle est la durée du programme ?", answer: "Six mois, dont quatre mois de formation mentorée puis deux mois de projet d'équipe. Avant cela, une piscine de deux semaines et une phase de préparation." },
        { question: "Comment les mentoringés sont-ils sélectionnés ?", answer: "Revue de candidature, travail préparatoire assigné, entretien en personne à Douala, puis une piscine de deux semaines. Chaque candidat reçoit une réponse." },
        { question: "Qu'est-ce qu'il me faut ?", answer: "Un ordinateur personnel, un compte GitHub, et la capacité à être présent tous les jours à Douala pendant six mois." },
        { question: "Combien ça coûte ?", answer: "Le programme est gratuit. Nous.existons parce que des entreprises et des sponsors donnent pour que la sélection reste stricte. Si vous souhaitez soutenir une promo, écrivez-nous." },
        { question: "Est-ce à distance ?", answer: "Non. La présence en personne, à Douala, tous les jours, fait partie de l'engagement — c'est ce qui rend le mentorat et le travail en équipe réels." },
      ],
    },
  },
  projects: {
    hero: {
      ticker: ["Projets & talents // CSL BRANDS", "Produits // liens directs"],
      eyebrow: "Projets et talents",
      titleLines: ["Des preuves,", "pas des promesses."],
      lead: "Découvrez les produits créés par CSL BRANDS. Chaque carte mène directement au produit présenté sur notre site principal.",
      figure: { label: "CSL BRANDS / Produits" },
    },
    teamProjects: {
      marker: "01",
      eyebrow: "Produits CSL BRANDS",
      title: "Des produits à découvrir dès maintenant.",
      lead: "Ces produits sont présentés dans la section « Ce que nous créons et faisons » de CSL BRANDS. Ouvrez chaque lien pour les découvrir.",
      items: projects,
    },
    footprint: {
      marker: "02",
      eyebrow: "Empreinte technique",
      title: "Découvrez tous nos produits.",
      lead: "Les liens ci-dessous proviennent de la vitrine des produits CSL BRANDS.",
      tableTitle: "Produits",
      columns: { repository: "Produit", classification: "Type", language: "Visiter" },
      rows: [
        { repository: "KURSA", classification: "Plateforme de formation", language: "Voir le produit", href: PROJECT_LINKS.kursa },
        { repository: "Shopikat", classification: "Plateforme e-commerce", language: "Voir le produit", href: PROJECT_LINKS.shopikat },
        { repository: "PrimeCorn", classification: "ERP / CRM", language: "Voir le produit", href: PROJECT_LINKS.primecorn },
        { repository: "TchopNjoh", classification: "Logiciel de restauration", language: "Voir le produit", href: PROJECT_LINKS.tchopnjoh },
      ],
      link: { label: "Ce que nous créons et faisons", href: "/#services" },
    },
    employers: {
      marker: "03",
      eyebrow: "Pour les employeurs",
      kicker: "Recruter sur des preuves",
      title: "Regardez comment l'équipe a travaillé. Puis rencontrez-la.",
      lead: "Visitez les produits ci-dessus pour découvrir le travail de CSL BRANDS. Pour parler du mentorat ou rencontrer de futurs talents, contactez notre équipe.",
      cta: { label: "Rencontrer des talents", target: "contact" },
    },
  },
  about: {
    hero: {
      ticker: ["À propos", "Douala // Cameroun"],
      eyebrow: "À propos",
      titleLines: ["À propos"],
      lead: "Le programme de mentorat CSL BRANDS existe parce que terminer un cours n'est pas la même chose que contribuer à une vraie équipe d'ingénierie.",
      leadExtra: "Le mentorat comble cet écart d'une seule façon : par du vrai travail — projets, revues, sprints, démos — en petites promotions, à Douala.",
    },
    where: {
      marker: "01",
      eyebrow: "Où nous travaillons",
      title: "Enracinés à Douala. Bâtis selon un standard plus large.",
      lead: "Nous travaillons depuis Douala, dans le Littoral — un écosystème dense de développeurs, designers et fondateurs s'est constitué autour de la ville. Être local est le principe : les mentoringés construisent des solutions à des problèmes qu'ils peuvent voir. Mais le standard n'est pas local — nous formons aux workflows des équipes professionnelles modernes, pour que le travail tienne la route en distribué et à l'international.",
      facts: [
        { label: "Base", value: "Douala, Littoral, Cameroun" },
        { label: "Format", value: "En personne, tous les jours" },
        { label: "Écosystème", value: "Littoral / Tech hub" },
      ],
      figure: { label: "Media / À propos / Douala" },
    },
    operation: {
      marker: "02",
      eyebrow: "Comment nous opérons",
      title: "Comme une équipe d'ingénierie, parce que c'en est une.",
      items: [
        { number: "01", title: "Petites promotions", text: "Une dizaine de places, sélectionnées délibérément. Tout le monde est connu, tout le monde est comptable." },
        { number: "02", title: "De vrais projets", text: "Le travail passe par des user stories, des sprints et des démos — pas des exercices avec corrigé." },
        { number: "03", title: "Culture de la revue", text: "Le code, les écrits et les décisions passent tous par une revue. Le retour est la norme, pas un événement." },
        { number: "04", title: "La communication est de l'ingénierie", text: "Points quotidiens, écrits d'avancement et démos publiques sont traités comme une discipline à part entière, pas comme un supplément." },
        { number: "05", title: "Documenté publiquement", text: "L'avancement des promotions est publié au fur et à mesure. Le registre fait foi." },
      ],
      rhythmTitle: "Le rythme",
      rhythmLead: "Autour des cours et des sprints, une cadence communautaire documentée garde le travail public et la pression honnête :",
      rhythm: ["Hackathons internes", "Démos publiques", "Le Book Club", "Rencontres communautaires"],
    },
    ecosystem: {
      marker: "03",
      eyebrow: "L'écosystème CSL BRANDS",
      title: "Un nom, trois fonctions.",
      lead: "Le programme de mentorat CSL BRANDS est entièrement détenu et opéré par CSL BRANDS. Cela compte concrètement : la formation se déroule à côté d'un vrai travail produit, et les personnes qui relisent votre code construisent des logiciels pour vivre.",
      entries: [
        { title: "Programme de mentorat CSL BRANDS", text: "Talents et communauté — le programme aujourd'hui, et l'environnement physique que nous construisons ensuite.", state: "Vous êtes ici" },
        { title: "CSL Studio", text: "Produit, marque et exécution logicielle — le contexte de projet réel et les mentors qui travaillent derrière la formation." },
        { title: "CSL Labs", text: "Un bras de création de ventures, prévu pour les idées et les équipes qui dépassent le programme." },
      ],
    },
    history: {
      marker: "04",
      eyebrow: "Histoire",
      title: "Le bilan jusqu'ici.",
      lead: "Des moments documentés, pas des jalons inventés après coup. Le journal complet vit dans nos mises à jour.",
      items: [
        { year: "2022", text: "CSL BRANDS fondée à Douala." },
        { year: "Mi-2025", text: "Première promotion — neuf mentoringés, plus de vingt cours structurés sur la phase initiale." },
        { year: "Août 2025", text: "La première promotion clôt sa phase de formation après plusieurs cycles de sprint sur des projets d'équipe." },
        { year: "Nov. 2025", text: "Cohort 01 sélectionné — dix mentoringés, avec un appel public à dix mentors." },
        { year: "Févr. 2026", text: "Domain-Driven Design, Scrum et Next.js ; préparation de la promotion suivante." },
      ],
    },
    team: {
      marker: "05",
      eyebrow: "L'équipe",
      title: "De vraies personnes, publiées correctement.",
      lead: "Les profils de l'équipe du programme et des mentors sont publiés ici avec le consentement de chaque personne. D'ici là, cette section reste honnête : pas de visages de banque d'images, pas de biographies inventées.",
      note: "Vous voulez savoir avec qui vous travailleriez ? Demandez-nous directement — nous répondons à chaque message.",
      figures: [
        { label: "Media / Équipe / Session mentor" },
        { label: "Media / Équipe / Mentoringés au travail" },
      ],
      cta: { label: "Contacter l'équipe", target: "contact" },
    },
    vision: {
      marker: "06",
      eyebrow: "La vision",
      kicker: "Nous construisons",
      title: "L'espace plus large : un foyer fiable pour les talents et les ventures à Douala.",
      lead: "Le programme de mentorat est la base opérationnelle. La phase suivante est l'environnement physique autour de lui — une infrastructure qui rend le travail technologique sérieux fiable à Douala.",
      features: [
        { title: "Énergie fiable", text: "Une électricité stable, y compris solaire — un travail qui ne s'arrête pas quand le réseau le fait." },
        { title: "Internet haut débit", text: "Une connectivité adaptée aux équipes distribuées et à la collaboration à distance." },
        { title: "Salles de formation", text: "Un espace dédié aux promotions, aux cours et aux ateliers." },
        { title: "Postes de travail", text: "Des environnements concentrés pour les équipes qui construisent au quotidien." },
        { title: "Capacité événementielle", text: "De la place pour les démos, les rencontres et la communauté qui se rassemble autour du travail." },
        { title: "Espace startups", text: "Un lieu où les équipes produit et les jeunes ventures grandissent côte à côte." },
      ],
      note: "Rien de tout cela n'est proposé comme installation disponible aujourd'hui. Nous annonçons chaque élément à son ouverture — pas avant.",
      cta: { label: "Rejoindre le travail, ou le soutenir", target: "contact" },
    },
  },
};

export default fr;
